import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';

import { NavbarItem } from '../models/navbar-item';
import { NavbarItems } from 'src/app/_layouts/navbar/navbar-item.constants';
import { AppConstants } from 'src/app/_apps/app.constants';
import { CompareUpperString2 } from '@common/services/utils.service';

@Injectable({
    providedIn: 'root'
})
export class ApplicationsService
{
    private static excludedIds: string[] = ['AUTH', 'DASH', 'ITSM'];
    private publicFilter: (ni: NavbarItem) => boolean = (ni: NavbarItem): boolean =>
        ni.isPublic;
    private adminFilter: (ni: NavbarItem) => boolean = (ni: NavbarItem): boolean =>
        ni.isAdmin;
    private dashboardFilter: (ni: NavbarItem) => boolean = (ni: NavbarItem): boolean =>
        ni.onDashboard && ni.enabled;

    public publicNavbarItems (forIcons: boolean = false): NavbarItem[]
    {
        if (forIcons)
        {
            return this.buildNavbar(this.publicFilter)
                .filter((ni: NavbarItem): boolean =>
                    !ApplicationsService.excludedIds.includes(ni.id)
                )
                .sort(CompareUpperString2<NavbarItem>((ni: NavbarItem): string =>
                    ni.id
                ));
        }

        return this.buildNavbar(this.publicFilter);
    }

    public adminNavbarItems (simplified: boolean = false): NavbarItem[]
    {
        if (simplified)
        {
            return this.buildNavbar(this.adminFilter)
                .filter((ni: NavbarItem): boolean =>
                    ni.id === 'BACK.DASH'
                );
        }

        return this.buildNavbar(this.adminFilter);
    }

    public dashboardNavbarItems = (): NavbarItem[] =>
        this.buildNavbar(this.dashboardFilter);

    public habilitationNavbarItems (): NavbarItem[]
    {
        return this.buildHabilitationsNavbar();
    }

    private buildNavbar (f: (ni: NavbarItem) => boolean): NavbarItem[]
    {
        const items = NavbarItems
            .filter(f)
            .filter((ni: NavbarItem): boolean =>
            {
                if (AppConstants.IsProductionStage)
                {
                    return ni.production < DateTime.utc();
                }

                if (AppConstants.IsStagingStage)
                {
                    return ni.staging < DateTime.utc();
                }

                return true;
            });
        items
            .sort((a: NavbarItem, b: NavbarItem): number =>
                a.order - b.order
            );

        return items;
    }

    private buildHabilitationsNavbar (): NavbarItem[]
    {
        const items = NavbarItems
            .filter((ni: NavbarItem): boolean =>
                ni.isPublic && ni.habilitation
            );
        items
            .sort((a: NavbarItem, b: NavbarItem): number =>
                this.habilitationOrder(b) - this.habilitationOrder(a)
            );

        return items;
    }

    // Ranking key of the habilitation views: deployed sections first, then upcoming ones.
    // It is never written back to NavbarItem.order: those instances are shared with the navbar,
    // which sorts ascending on order (see buildNavbar), so assigning 100/50 here would permanently
    // reorder the menu until the next page reload.
    private habilitationOrder (ni: NavbarItem): number
    {
        return ni.isDeployed ? 100 : ni.soon ? 50 : ni.order;
    }
}
